import Papa from 'papaparse';

import { BulkAssetRow } from '../types';

interface ParseBulkAssetCsvOptions {
  includeRowsMissingVin?: boolean;
}

const COLUMN_HEADERS = {
  accountSourceId: 'accountid',
  depotFmsId: 'fmsid',
  vin: 'vin',
  unitNumber: 'unit',
  odometer: 'odometer',
  engineHours: 'enginehours',
  fleetInfo: 'fleetinfo',
};

const REQUIRED_HEADERS = [
  COLUMN_HEADERS.accountSourceId,
  COLUMN_HEADERS.depotFmsId,
  COLUMN_HEADERS.vin,
  COLUMN_HEADERS.unitNumber,
];

const normalizeHeader = (header: string): string =>
  header.toLowerCase().replace(/[^a-z0-9]/g, '');

const cell = (
  row: Record<string, unknown>,
  normalizedHeader: string,
): string => {
  const key = Object.keys(row).find(
    (k) => normalizeHeader(k) === normalizedHeader,
  );
  return key ? String(row[key] ?? '').trim() : '';
};

// Coerce an optional integer cell (e.g. Odometer "150,987") to a number,
// stripping thousands separators. Empty/non-numeric cells become undefined.
const toInteger = (value: string): number | undefined => {
  const normalized = value.trim().replace(/[,\s]/g, '');
  if (!normalized || !/^\d+$/.test(normalized)) return undefined;
  return Number(normalized);
};

export const hasRequiredHeaders = (file: File): Promise<boolean> =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      preview: 1,
      complete: (result) => {
        const fields = (result.meta.fields ?? []).map(normalizeHeader);
        resolve(REQUIRED_HEADERS.every((header) => fields.includes(header)));
      },
      error: (error) =>
        reject(new Error(`Failed to parse CSV: ${error.message}`)),
    });
  });

export const parseBulkAssetCsv = (
  file: File,
  options: ParseBulkAssetCsvOptions = {},
): Promise<BulkAssetRow[]> =>
  new Promise((resolve, reject) => {
    const { includeRowsMissingVin = false } = options;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = (result.data as Record<string, unknown>[]).reduce<
          BulkAssetRow[]
        >((acc, raw) => {
          const accountSourceId = cell(raw, COLUMN_HEADERS.accountSourceId);
          const depotFmsId = cell(raw, COLUMN_HEADERS.depotFmsId);
          const vin = cell(raw, COLUMN_HEADERS.vin);
          const unitNumber = cell(raw, COLUMN_HEADERS.unitNumber);
          const hasRequiredBaseFields =
            accountSourceId && depotFmsId && unitNumber;

          if (hasRequiredBaseFields && (vin || includeRowsMissingVin)) {
            acc.push({
              accountSourceId,
              depotFmsId,
              vin,
              unitNumber,
              mileage: toInteger(cell(raw, COLUMN_HEADERS.odometer)),
              engineHours: toInteger(cell(raw, COLUMN_HEADERS.engineHours)),
              fleetInfo: cell(raw, COLUMN_HEADERS.fleetInfo) || undefined,
            });
          }

          return acc;
        }, []);

        resolve(rows);
      },
      error: (error) =>
        reject(new Error(`Failed to parse CSV: ${error.message}`)),
    });
  });
